<template>
  <div
    ref="topBar"
    class="rf-top-nav border-beige-10 fixed top-0 left-0 z-20 mr-0.5 w-full border border-solid bg-white pr-9 pl-5"
  >
    <div data-rf-zone="app-notification-bar"></div>
    <div class="flex h-12 items-center">
      <div class="h-full">
        <a href="/" class="flex h-full w-full pr-4">
          <img
            class="img my-auto h-5 max-w-max!"
            alt="Recurly Engage logo"
            src="../../../assets/images/engage.svg"
          />
        </a>
      </div>
      <div class="flex-grow"></div>
      <template v-if="biggerOrEqualSm">
        <div class="rf-top-menu-list flex grow-0 items-center">
          <div class="rf-top-menu rf-top-menu-org-switch mr-1">
            <v-menu
              v-if="userStore.isCurrentSuperAdmin"
              contentClass="rf-top-dropdown-app"
              offsetY
              openOnClick
              closeOnContentClick
              :disabled="appSwitcherDisabled"
            >
              <template #activator="{ props, isActive }">
                <button
                  class="body-text text-beige-90 inline-flex items-center gap-2"
                  v-bind="props"
                >
                  {{ orgsStore.currOrg.name || "All" }}
                  <Transition mode="out-in">
                    <RfChevronMiniClosedIcon v-if="!isActive" class="fill-beige-50! h-7! w-7!" />
                    <RfChevronMiniOpenIcon v-else class="fill-beige-50! h-7! w-7!" />
                  </Transition>
                </button>
              </template>
              <v-list>
                <v-list-item
                  v-for="(org, index) in orgSelector"
                  :key="index"
                  class="rf-topbar-list-item"
                  :class="orgsStore.currOrg.key === org.key ? 'org-selected' : ''"
                  @click="switchOrg(org.key)"
                >
                  <v-list-item-title>
                    <span>{{ org.name }}</span>
                    <v-icon v-if="orgsStore.currOrg.key === org.key" color="primary" size="18"
                      >md:check_circle</v-icon
                    >
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <div v-else class="bg-yellow text-black-1 rounded px-1 text-sm">
              {{ orgsStore.currAppUserOrg.name }}
            </div>
          </div>
          <div class="rf-top-menu rf-top-menu-user">
            <v-menu contentClass="rf-top-dropdown-user" offsetY>
              <template #activator="{ props, isActive }">
                <button class="body-text inline-flex items-center gap-1" v-bind="props">
                  <RfPersonIcon class="h-6! w-6!" />
                  <Transition mode="out-in">
                    <RfChevronMiniClosedIcon v-if="!isActive" class="fill-beige-50! h-7! w-7!" />
                    <RfChevronMiniOpenIcon v-else class="fill-beige-50! h-7! w-7!" />
                  </Transition>
                </button>
              </template>
              <v-list>
                <v-list-item v-if="canAccessCompanySettings" class="rf-topbar-list-item">
                  <v-list-item-title>
                    <router-link :to="{ path: '/settings/company' }">Company Settings</router-link>
                  </v-list-item-title>
                </v-list-item>
                <v-list-item v-if="userStore.isUserCorpIt" class="rf-topbar-list-item">
                  <v-list-item-title>
                    <router-link :to="{ path: '/settings/company-access' }"
                      >Company Access</router-link
                    >
                  </v-list-item-title>
                </v-list-item>
                <v-list-item v-if="userStore.isUserSuperAdmin" class="rf-topbar-list-item">
                  <v-list-item-title>
                    <router-link :to="{ path: '/settings/global' }">Global Settings</router-link>
                  </v-list-item-title>
                </v-list-item>
                <v-list-item v-if="userStore.isUserSuperAdmin" class="rf-topbar-list-item">
                  <v-list-item-title>
                    <router-link :to="{ path: '/audits' }">Audit Logs</router-link>
                  </v-list-item-title>
                </v-list-item>
                <v-list-item class="rf-topbar-list-item">
                  <v-list-item-title>
                    <button @click="userStore.logout()">Logout</button>
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </div>
      </template>
      <RfSwitch
        v-if="classicView.show"
        v-model="classicViewStore.classicView[classicView.key]"
        class="mx-4"
        title="Classic view"
        @update:modelValue="v => setUserSetting(classicView.setting, v)"
      />
    </div>
  </div>
</template>

<script>
import RfSwitch from "@/components/inputs/RfSwitch.vue";
import RfPersonIcon from "@/components/icons/RfPersonIcon.vue";
import RfChevronMiniOpenIcon from "@/components/icons/RfChevronMiniOpenIcon.vue";
import RfChevronMiniClosedIcon from "@/components/icons/RfChevronMiniClosedIcon.vue";
import { useUserSettings } from "@/utils/composables/useUserSettings";
import { useClassicViewStore } from "@/pinia/classicViewStore";
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useUsersStore } from "@/pinia/usersStore";
import { useAppsStore } from "@/pinia/appsStore";
import { useUserStore } from "@/pinia/userStore";
import { useOrgsStore } from "@/pinia/orgsStore";
import { useLegacyLastErrorStore } from "@/pinia/legacyLastErrorStore";
import { biggerOrEqualSm } from "@/utils/composables/useBreakpoint";

export default {
  components: { RfSwitch, RfPersonIcon, RfChevronMiniClosedIcon, RfChevronMiniOpenIcon },
  setup: () => {
    const appsStore = useAppsStore();
    const userStore = useUserStore();
    const { getUserSetting, setUserSetting } = useUserSettings();
    const root = document.querySelector(":root");
    const topBar = ref();

    const resizeObserver = new ResizeObserver(([navbar]) =>
      root.style.setProperty("--main-margin-top", `${navbar.target.clientHeight || 48}px`),
    );

    onMounted(() => {
      if (topBar.value) resizeObserver.observe(topBar.value);
    });
    onUnmounted(() => resizeObserver.disconnect());

    const canAccessCompanySettings = computed(() => {
      // Super admins always have access
      if (userStore.isCurrentSuperAdmin) return true;

      // Check if user has multi-company access
      return userStore.currUser?.has_multi_company_access || false;
    });

    return {
      topBar,
      classicViewStore: useClassicViewStore(),
      usersStore: useUsersStore(),
      userStore,
      orgsStore: useOrgsStore(),
      legacyLastErrorStore: useLegacyLastErrorStore(),
      currApp: computed(() => appsStore.app),
      canAccessCompanySettings,
      biggerOrEqualSm,
      getUserSetting,
      setUserSetting,
    };
  },
  computed: {
    orgSelector() {
      return [{ name: "All", key: null }, ...(this.orgsStore.orgs || [])];
    },
    appSwitcherDisabled() {
      return this.legacyLastErrorStore.loading > 0 || this.$route.path === "/newapp";
    },
    classicView() {
      return { show: false, key: null, setting: null };
    },
  },
  watch: {
    currApp: {
      handler(to, from) {
        if (to?.id === from?.id) return;
        this.orgsStore.getOrgs(to.id);
        if (!this.classicView.show) return;
        this.classicViewStore.classicView[this.classicView.key] = this.getUserSetting(
          this.classicView.setting,
        );
      },
      immediate: true,
    },
  },
  methods: {
    async switchOrg(orgKey) {
      this.userStore.currUser.settings[this.currApp.id].org_key = orgKey;
      await this.usersStore.updateUser({ user: this.userStore.currUser });
      await this.userStore.getUser();
      const redirectPage = {
        "/apps/:aid/experiences/:sid": `/apps/${this.$route.params.aid}/experiences`,
        "/apps/:aid/retentions/:pid": `/apps/${this.$route.params.aid}/prompts`,
      };
      const result = this.$route.matched.find(({ path }) => {
        const items = Object.keys(redirectPage);
        const res = items.find(item => path.match(item));
        return res;
      });
      if (result) {
        window.location.replace(redirectPage[result.path]);
      } else {
        window.location.reload();
      }
    },
  },
};
</script>

<style lang="scss">
.rf-topbar-list-item {
  &.org-selected {
    background: #eee;
    font-weight: bold;
  }
  .v-list-item-title {
    font-size: 1em;
    display: flex;
    justify-content: space-between;
    > span {
      padding-right: 10px;
    }
  }
}

.rf-top-dropdown-app {
  .v-list {
    padding: 0;
  }
  .v-list-item {
    min-width: 160px;
  }
}

.rf-top-dropdown-user,
.rf-top-dropdown-app {
  &.v-menu__content {
    box-shadow: 0 1px 4px 1px rgba(0, 0, 0, 0.14);
  }
}

.rf-top-dropdown-user {
  .v-list-item {
    min-height: auto;
    padding: 10px 15px;
    min-width: 100px;
    text-align: left;

    a {
      color: #333 !important;
      display: inline-block;
      width: 100%;
    }
  }
}
</style>

<style lang="scss" scoped>
.add-new-app-list {
  font-weight: bold;
  color: #3096ed;
}
.rf-top-nav {
  box-shadow: 0px 4px 9px rgba(220, 222, 226, 0.25);
}

.rf-top-menu-list {
  .rf-top-menu {
    align-items: center;
    justify-content: center;
    display: flex;

    button {
      color: #212121 !important;
    }
  }
  .rf-top-menu-org-switch,
  .rf-top-menu-app-switch {
    button {
      padding: 10px 16px 10px 0;
    }
  }
}
</style>
